/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import ArrowLeft from './icons/ArrowLeft';
import BlockContent from '@sanity/block-content-to-react';
import Slider from 'react-slick';
import BackInStock from './BackInStock';
import AddToWishList from './AddToWishList';
import { Link } from 'gatsby';
import {
  ProductProvider,
  ProductPrice,
  AddToCartButton,
  useProduct,
} from '@shopify/hydrogen-react';
import { useShopifyProduct } from '../hooks/useShopifyProduct';
import { useContext } from 'react';
import Context from '../context/Context';

import classNames from 'classnames';
import richText from '../serializers/richText';

const settings = {
  dots: true,
  infinite: true,
  speed: 400,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 10000,
  cssEase: 'ease',
};

const ProductHeroInterior = ({
  title,
  designer,
  productImages = [],
  shopifyImage,
  components = [],
  details = [],
  backToCollection,
  productId,
}) => {
  const { product, variants, options, selectedVariant, setSelectedVariant } =
    useProduct();
  const { setCartOpen } = useContext(Context);

  useEffect(() => {
    if (variants && variants.length > 0 && !selectedVariant) {
      const firstAvailableVariant = variants.find(
        variant => variant.availableForSale
      );
      if (firstAvailableVariant) {
        setSelectedVariant(firstAvailableVariant);
      } else if (variants.length > 0) {
        setSelectedVariant(variants[0]);
      }
    }
  }, [variants, selectedVariant, setSelectedVariant]);

  return (
    <section className="product__hero df fdc fdr--lg">
      <aside
        className={`product__hero-images p2 pb5 p5--md p7--lg p10--xl pr ${
          backToCollection ? 'pt5' : ''
        }`}
      >
        {backToCollection && (
          <div className="product__hero-back pa sans--sm">
            <Link to={backToCollection.slug} className="link--underline df aic">
              <ArrowLeft />
              Back to {backToCollection.title}
            </Link>
          </div>
        )}
        <div className="product__hero-images-carousel b--dark-brown">
          {productImages && productImages.length > 0 ? (
            <Slider {...settings}>
              {productImages.map(item => {
                return (
                  <img
                    key={item._key}
                    className="product__hero-slide__img"
                    src={item.url}
                    alt={`Product Image for ${title}`}
                  />
                );
              })}
            </Slider>
          ) : (
            <img className="db" src={shopifyImage} alt={title} />
          )}
        </div>
      </aside>
      <aside className="product__hero-details p2 pb10  p5--md p7--lg p10--xl pr">
        <div className="title df mb1 aib ">
          <p className="mono--sm mono--md--md mr1 uppercase spotlight-index__label">
            Product
          </p>
          <h1 className="sans--md">{product?.title || title}</h1>
        </div>
        <div className="title df mb1  aib">
          <p className="mono--sm mono--md--md mr1 uppercase spotlight-index__label">
            Designer
          </p>
          {components &&
          components.find(item => item._type === 'featuredDesigner') ? (
            <Link
              className="link--underline"
              to={`/designers${
                components.find(item => item._type === 'featuredDesigner')
                  .designer.slug
              }`}
            >
              {designer}
            </Link>
          ) : (
            <p className="sans--sm">{designer}</p>
          )}
        </div>
        <div className="title df mb1  aib">
          <p className="mono--sm mono--md--md mr1 uppercase spotlight-index__label">
            Price
          </p>
          <div className="df sans--sm aic">
            <ProductPrice
              data={product}
              variantId={selectedVariant?.id}
              priceType="compareAt"
              className="pr1 strike color--light-brown"
              withoutTrailingZeros
            />
            <ProductPrice
              data={product}
              variantId={selectedVariant?.id}
              withoutTrailingZeros
            />
            <span className="pl1">USD</span>
          </div>
        </div>
        {options?.map(
          option =>
            option.values.length > 1 && (
              <div
                key={option.name}
                className="product__hero-options title df mb1  aib"
              >
                <p className="mono--sm mono--md--md mr1 uppercase spotlight-index__label">
                  {option.name}
                </p>
                <div className="product__hero-options-wrapper pr df">
                  {option.values.map(value => {
                    const variantForValue = variants.find(variant =>
                      variant.selectedOptions.some(
                        opt => opt.name === option.name && opt.value === value
                      )
                    );

                    return (
                      <button
                        key={`${option.name}-${value}`}
                        onClick={() => {
                          if (variantForValue?.availableForSale) {
                            setSelectedVariant(variantForValue);
                          }
                        }}
                        className={classNames(
                          'sans--sm mr2 df aic collection__filter',
                          {
                            selected: selectedVariant?.selectedOptions.some(
                              opt =>
                                opt.name === option.name && opt.value === value
                            ),
                            disabled: !variantForValue?.availableForSale,
                          }
                        )}
                        disabled={!variantForValue?.availableForSale}
                      >
                        <div
                          style={{
                            backgroundColor:
                              selectedVariant?.selectedOptions.some(
                                opt =>
                                  opt.name === option.name &&
                                  opt.value === value
                              )
                                ? 'currentcolor'
                                : null,
                          }}
                          className="circle collection__filter-circle mr1"
                        />{' '}
                        {value}
                      </button>
                    );
                  })}
                </div>
              </div>
            )
        )}
        <div className="product__hero-options title df mb1  aib">
          <p className="mono--sm mono--md--md mr1 uppercase spotlight-index__label">
            Wish List
          </p>
          <div className="product__hero-options-wrapper pr df -translate-x-5">
            <AddToWishList
              pdp
              productId={productId}
              variantId={selectedVariant?.id}
            />
          </div>
        </div>

        {details && details.length > 0 && (
          <div className="product__hero-description mt5 mt10--lg">
            <p className="mono--md mono--lg--md uppercase bb--dark-brown p1">
              Product Details
            </p>
            {details.map((detail, index) => {
              return (
                <div
                  key={detail._key}
                  className={classNames('title df aib pt1 pl1', {
                    pb1: index === details.length - 1,
                  })}
                >
                  <p className="mono--sm mono--md--md mr1 uppercase spotlight-index__label">
                    {detail.label}
                  </p>
                  <div className="sans--sm">
                    <BlockContent
                      blocks={detail.description}
                      serializers={richText}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <section className="product__add-to-cart df pa">
          <AddToCartButton
            variantId={selectedVariant?.id}

            className="product__add-to-cart-button tl pl1 pl2--sm"
            disabled={
              !selectedVariant?.availableForSale || !selectedVariant?.id
            }
            onClick={() => {
              setTimeout(() => {
                setCartOpen(true);
              }, 200);
            }}
          >
            {selectedVariant?.availableForSale ? 'Add to Cart' : 'Out of Stock'}
          </AddToCartButton>
        </section>
        {!selectedVariant?.availableForSale && selectedVariant?.id && (
          <BackInStock variantId={selectedVariant.id} />
        )}
      </aside>
    </section>
  );
};

const ProductHero = ({
  title,
  designer,
  productImages = [],
  shopifyImage,
  components = [],
  productId,
  details = [],
  backToCollection,
  slug,
}) => {
  const product = useShopifyProduct({ handle: slug });

  return product ? (
    <ProductProvider data={product}>
      <ProductHeroInterior
        title={title}
        designer={designer}
        productImages={productImages}
        shopifyImage={shopifyImage}
        components={components}
        details={details}
        backToCollection={backToCollection}
        slug={slug}
        productId={productId}
      />
    </ProductProvider>
  ) : (
    <section className="product__hero df fdc fdr--lg">
      <aside className="product__hero-images p2 pb5 p5--md p7--lg p10--xl pr"></aside>
      <aside className="product__hero-details p2 pb10  p5--md p7--lg p10--xl pr">
        <p>Loading...</p>
      </aside>
    </section>
  );
};

export default ProductHero;
